import {useCallback} from 'react'
import {useDispatch} from 'react-redux'
import {nanoid} from '@reduxjs/toolkit'
import {useUrlUpdater} from 'components/data/WithUrlUpdater'
import {getOverlayInfoFromUrlAndStorage} from 'hooks/useSyncOverlayState'
import {
  DateGuestPickerSource,
  Overlay,
  OverlayType
} from 'modules/overlay/types'
import {setOpenDatePickerType} from 'modules/searchBox/slice'
import {append} from 'ramda'

import {safelySetStorageItem} from '@vio/core/src/_web/utils/persistence'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContextList,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import {DatePickerType} from '@vio/core/src/datePicker/types'

export const useShowHotelDatePickerOverlay = () => {
  const dispatch = useDispatch()
  const showOverlay = useShowOverlay()

  const showDatePickerOverlay = useCallback(
    (
      type: DatePickerType,
      team: Team = Team.Default,
      source: DateGuestPickerSource
    ) => {
      dispatch(setOpenDatePickerType(type))

      showOverlay({
        type: OverlayType.HotelDatePicker,
        entity: Entity.DatePicker,
        component: type,
        team,
        payload: {source},
        params: {
          source
        }
      })
    },
    [dispatch, showOverlay]
  )

  return showDatePickerOverlay
}

export const useShowDatePickerOverlay = () => {
  const dispatch = useDispatch()
  const showOverlay = useShowOverlay()

  const showDatePickerOverlay = useCallback(
    (type: DatePickerType, team: Team = Team.Default) => {
      dispatch(setOpenDatePickerType(type))

      showOverlay({
        type: OverlayType.DatePicker,
        entity: Entity.DatePicker,
        component: type,
        team
      })
    },
    [dispatch, showOverlay]
  )

  return showDatePickerOverlay
}

interface ShowOverlayPayload<Type extends OverlayType> {
  type: Type
  entity?: Entity
  params?: Omit<Extract<Overlay, {type: Type}>, 'type'> // Params will be enforced based on type, this relies on Overlay union correctness
  component?: string
  analyticsContext?: AnalyticsContextList
  team?: Team
  payload?: Record<string, unknown>
}
const useShowOverlay = () => {
  const {updateLocationShallow} = useUrlUpdater()
  const dispatch = useDispatch()

  const showOverlay = useCallback(
    <T extends OverlayType>({
      type,
      params,
      entity,
      component,
      analyticsContext,
      team = Team.Default,
      payload
    }: ShowOverlayPayload<T>) => {
      const id = nanoid(6)

      const {activeOverlay, overlays} = getOverlayInfoFromUrlAndStorage()

      const activeIndex = overlays.findIndex(o => o.id === activeOverlay)

      const activeOverlays = overlays.slice(0, activeIndex + 1)
      const updatedOverlays = append({type, id, ...params}, activeOverlays)

      // Store the stack to be hydrated on pop/reload
      safelySetStorageItem(
        sessionStorage,
        'overlays',
        JSON.stringify(updatedOverlays)
      )

      // Set active overlay id in url
      updateLocationShallow(
        {
          overlay: id
        },
        {basePath: window.location.pathname}
      )

      if (typeof entity !== 'undefined') {
        dispatch(
          trackEvent({
            category: Category.System,
            entity,
            action: Action.Displayed,
            component,
            analyticsContext,
            team,
            payload
          })
        )
      }

      return {
        activeOverlay: id,
        overlays: updatedOverlays
      }
    },
    [dispatch, updateLocationShallow]
  )

  return showOverlay
}

export default useShowOverlay
